import { Routes } from '@angular/router';
import { authGuard } from './core/auth/auth.guard';
import { accessGuard } from './core/auth/access.guard';
import { maintenanceGuard } from './core/auth/maintenance.guard';
import { PAGE_ACCESS } from './core/auth/access-control';
import { CommissionApprovalPageComponent } from './features/approvals/commission-approval/commission-approval-page.component';
import { ExpenseApprovalPageComponent } from './features/approvals/expense-approval/expense-approval-page.component';
import { FinancialAdjustmentApprovalPageComponent } from './features/approvals/financial-adjustment-approval/financial-adjustment-approval-page.component';
import { RefundApprovalPageComponent } from './features/approvals/refund-approval/refund-approval-page.component';
import { CashFlowPageComponent } from './features/accounting/pages/cash-flow-page.component';
import { ExpensePageComponent } from './features/accounting/pages/expense-page.component';
import { IncomePageComponent } from './features/accounting/pages/income-page.component';
import { LoginComponent } from './features/auth/pages/login.component';
import { MaintenanceComponent } from './features/maintenance/pages/maintenance.component';
import { BookingDetailPageComponent } from './features/bookings/pages/booking-detail-page.component';
import { ExpenseRequestsPageComponent } from './features/expenses/pages/expense-requests-page.component';
import { BookingsPageComponent } from './features/bookings/pages/bookings-page.component';
import { GroupBookingsPageComponent } from './features/bookings/pages/group-bookings-page.component';
import { ToursPageComponent } from './features/tours/pages/tours-page.component';
import { TourDetailPageComponent } from './features/tours/pages/tour-detail-page.component';
import { CommissionsPageComponent } from './features/commissions/pages/commissions-page.component';
import { CommissionReviewPageComponent } from './features/commissions/pages/commission-review-page.component';
import { CommissionRecoveriesPageComponent } from './features/commissions/pages/commission-recoveries-page.component';
import { CustomersPageComponent } from './features/customers/pages/customers-page.component';
import { AccountingDashboardComponent } from './features/dashboard/pages/accounting-dashboard.component';
import { DashboardComponent } from './features/dashboard/pages/dashboard.component';
import { SalesDashboardComponent } from './features/dashboard/pages/sales-dashboard.component';
import { ExpensesPageComponent } from './features/expenses/pages/expenses-page.component';
import { GeneralExpensesPageComponent } from './features/expenses/pages/general-expenses-page.component';
import { ExpenseReviewPageComponent } from './features/expenses/pages/expense-review-page.component';
import { PaymentReviewPageComponent } from './features/payments/pages/payment-review-page.component';
import { PaymentsPageComponent } from './features/payments/pages/payments-page.component';
import { RefundReviewPageComponent } from './features/refunds/pages/refund-review-page.component';
import { MainLayout } from './layout/main-layout/main-layout.component';

const APP_NAME = 'All To Trips';
const pageTitle = (pageName: string): string => `${APP_NAME} | ${pageName}`;

export const routes: Routes = [
  {
    path: 'login',
    component: LoginComponent,
    title: pageTitle('เข้าสู่ระบบ'),
    canActivate: [maintenanceGuard],
  },
  {
    path: 'maintenance',
    component: MaintenanceComponent,
    title: pageTitle('ปิดปรับปรุงระบบ'),
    canActivate: [maintenanceGuard],
  },
  {
    path: '',
    component: MainLayout,
    canActivate: [maintenanceGuard],
    canActivateChild: [authGuard],
    children: [
      {
        path: 'dashboard',
        component: DashboardComponent,
        title: pageTitle('ภาพรวมระบบ'),
        canActivate: [accessGuard],
        data: { access: PAGE_ACCESS.dashboard },
      },
      {
        path: 'dashboard/sales',
        component: SalesDashboardComponent,
        title: pageTitle('แดชบอร์ดฝ่ายขาย'),
        canActivate: [accessGuard],
        data: { access: PAGE_ACCESS.salesDashboard },
      },
      {
        path: 'dashboard/accounting',
        component: AccountingDashboardComponent,
        title: pageTitle('แดชบอร์ดบัญชี'),
        canActivate: [accessGuard],
        data: { access: PAGE_ACCESS.accountingDashboard },
      },
      {
        path: 'accounting/cash-flow',
        component: CashFlowPageComponent,
        title: pageTitle('รายการรับ–จ่าย'),
        canActivate: [accessGuard],
        data: { access: PAGE_ACCESS.cashFlow },
      },
      {
        path: 'accounting/income',
        component: IncomePageComponent,
        title: pageTitle('รายการรายรับ'),
        canActivate: [accessGuard],
        data: { access: PAGE_ACCESS.income },
      },
      {
        path: 'accounting/expense',
        component: ExpensePageComponent,
        title: pageTitle('รายการรายจ่าย'),
        canActivate: [accessGuard],
        data: { access: PAGE_ACCESS.accountingExpense },
      },
      {
        path: 'customers',
        component: CustomersPageComponent,
        title: pageTitle('ลูกค้า'),
        canActivate: [accessGuard],
        data: { access: PAGE_ACCESS.customers },
      },
      {
        path: 'bookings',
        component: BookingsPageComponent,
        title: pageTitle('รายการจองจอยทัวร์'),
        canActivate: [accessGuard],
        data: { access: PAGE_ACCESS.bookings },
      },
      {
        path: 'bookings/group',
        component: GroupBookingsPageComponent,
        title: pageTitle('รายการจองเหมากรุ๊ป'),
        canActivate: [accessGuard],
        data: { access: PAGE_ACCESS.groupBookings },
      },
      {
        path: 'tours',
        component: ToursPageComponent,
        title: pageTitle('รายการทัวร์'),
        canActivate: [accessGuard],
        data: { access: PAGE_ACCESS.tours },
      },
      {
        path: 'tours/:id',
        component: TourDetailPageComponent,
        title: pageTitle('รายละเอียดทัวร์'),
        canActivate: [accessGuard],
        data: { access: PAGE_ACCESS.tours },
      },
      {
        path: 'expenses/requests',
        component: ExpenseRequestsPageComponent,
        title: pageTitle('รายการเบิกทั้งหมด'),
        canActivate: [accessGuard],
        data: { access: PAGE_ACCESS.expenseRequests },
      },
      {
        path: 'expenses/general',
        component: GeneralExpensesPageComponent,
        title: pageTitle('รายการเบิกค่าใช้จ่ายทั่วไป'),
        canActivate: [accessGuard],
        data: { access: PAGE_ACCESS.generalExpenses },
      },
      {
        path: 'bookings/:id',
        component: BookingDetailPageComponent,
        title: pageTitle('รายละเอียด Booking'),
        canActivate: [accessGuard],
        data: { access: PAGE_ACCESS.bookings },
      },
      {
        path: 'payments',
        component: PaymentsPageComponent,
        title: pageTitle('การชำระเงิน'),
        canActivate: [accessGuard],
        data: { access: PAGE_ACCESS.payments },
      },
      {
        path: 'payments/review',
        component: PaymentReviewPageComponent,
        title: pageTitle('ตรวจสอบการชำระ'),
        canActivate: [accessGuard],
        data: { access: PAGE_ACCESS.paymentReview },
      },
      {
        path: 'expenses',
        component: ExpensesPageComponent,
        title: pageTitle('ใบขอเบิก'),
        canActivate: [accessGuard],
        data: { access: PAGE_ACCESS.expenses },
      },
      {
        path: 'expenses/review',
        component: ExpenseReviewPageComponent,
        title: pageTitle('ตรวจสอบใบขอเบิก'),
        canActivate: [accessGuard],
        data: { access: PAGE_ACCESS.expenseReview },
      },
      {
        path: 'commissions',
        component: CommissionsPageComponent,
        title: pageTitle('ค่าคอมมิชชั่น'),
        canActivate: [accessGuard],
        data: { access: PAGE_ACCESS.commissions },
      },
      {
        path: 'commissions/review',
        component: CommissionReviewPageComponent,
        title: pageTitle('ตรวจสอบค่าคอมมิชชั่น'),
        canActivate: [accessGuard],
        data: { access: PAGE_ACCESS.commissionReview },
      },
      {
        path: 'commissions/recoveries',
        component: CommissionRecoveriesPageComponent,
        title: pageTitle('ยอดหักคืนค่าคอม'),
        canActivate: [accessGuard],
        data: { access: PAGE_ACCESS.commissionRecoveries },
      },
      {
        path: 'refunds/review',
        component: RefundReviewPageComponent,
        title: pageTitle('ตรวจสอบการคืนเงิน'),
        canActivate: [accessGuard],
        data: { access: PAGE_ACCESS.refundReview },
      },
      {
        path: 'approvals/expenses',
        component: ExpenseApprovalPageComponent,
        title: pageTitle('อนุมัติใบขอเบิก'),
        canActivate: [accessGuard],
        data: { access: PAGE_ACCESS.expenseApproval },
      },
      {
        path: 'approvals/commissions',
        component: CommissionApprovalPageComponent,
        title: pageTitle('อนุมัติค่าคอมมิชชั่น'),
        canActivate: [accessGuard],
        data: { access: PAGE_ACCESS.commissionApproval },
      },
      {
        path: 'approvals/financial-adjustments',
        component: FinancialAdjustmentApprovalPageComponent,
        title: pageTitle('อนุมัติการปรับวงเงิน'),
        canActivate: [accessGuard],
        data: { access: PAGE_ACCESS.financialAdjustmentApproval },
      },
      {
        path: 'approvals/refunds',
        component: RefundApprovalPageComponent,
        title: pageTitle('อนุมัติการคืนเงิน'),
        canActivate: [accessGuard],
        data: { access: PAGE_ACCESS.refundApproval },
      },
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'dashboard',
      },
    ],
  },
  {
    path: '**',
    redirectTo: 'dashboard',
  },
];
